// UI dictionaries (TVirt idiom, ratified there 2026-07-12; adopted for TVStor
// M1): resolved client-side — the chosen language lives in localStorage
// ('tvstor-lang'), no URL prefix. This file only recomposes ./sections/* into
// the runtime shape — the per-area section files are the source of truth.
//
// M1 locales: en + it, with `it` typed `typeof en` so key parity is enforced
// at compile time. TVirt's wave-1 locales (es/pt/fr/de + meta sidecars) join
// when the translation governance lands here too.

import * as common from './sections/common'
import * as auth from './sections/auth'
import * as catalogsources from './sections/catalogsources'
import * as cluster from './sections/cluster'
import * as credentials from './sections/credentials'
import * as explorer from './sections/explorer'
import * as ioserverpairs from './sections/ioserverpairs'
import * as monitoring from './sections/monitoring'
import * as objectstorage from './sections/objectstorage'
import * as protection from './sections/protection'
import * as rbac from './sections/rbac'
import * as realms from './sections/realms'
import * as scopes from './sections/scopes'
import * as storage from './sections/storage'
import * as system from './sections/system'
import * as tasks from './sections/tasks'

export const LOCALES = ['en', 'it'] as const

export type Lang = (typeof LOCALES)[number]

// The source language of every string (TVirt governance #171, adopted): parity
// is measured against ITALIAN, not against English.
export const SOURCE_LANG: Lang = 'it'

export const isLang = (value: unknown): value is Lang =>
  typeof value === 'string' && (LOCALES as readonly string[]).includes(value)

// In ALPHABETICAL code order (TVirt rule, Luca 2026-08-03): the code is what
// the user reads first in the label.
export const LANGUAGES: { code: Lang; label: string }[] = [
  { code: 'en', label: 'EN - English' },
  { code: 'it', label: 'IT - Italiano' }
]

const en = {
  ...common.en,
  ...auth.en,
  ...catalogsources.en,
  ...cluster.en,
  ...credentials.en,
  ...explorer.en,
  ...ioserverpairs.en,
  ...monitoring.en,
  ...objectstorage.en,
  ...protection.en,
  ...rbac.en,
  ...realms.en,
  ...scopes.en,
  ...storage.en,
  ...system.en,
  ...tasks.en
}

const it: typeof en = {
  ...common.it,
  ...auth.it,
  ...catalogsources.it,
  ...cluster.it,
  ...credentials.it,
  ...explorer.it,
  ...ioserverpairs.it,
  ...monitoring.it,
  ...objectstorage.it,
  ...protection.it,
  ...rbac.it,
  ...realms.it,
  ...scopes.it,
  ...storage.it,
  ...system.it,
  ...tasks.it
}

export type Dictionary = typeof en

export const dictionaries: Record<Lang, Dictionary> = { en, it }
